import React from 'react';
import { Flex, Link, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FooterLinks } from '../../constants/FooterLinks';
import { useAuth } from '../../context/AuthContext.tsx';
import { IUserProfile } from '../../interfaces';

const supportRequestLinks = {
  de: 'https://g2sb6.share-eu1.hsforms.com/2miQOi4EORFKy356qeJkiFQ',
  en: 'https://g2sb6.share-eu1.hsforms.com/27oysWysiTmOsBwryGAeGmQ',
};

// src/utils/platformDetection.ts
export const getPlatformInfo = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Detect platform
  const platform = 'web';

  // Detect browser for web platform
  let browser = 'unknown';
  if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
    browser = 'chrome';
  } else if (userAgent.includes('firefox')) {
    browser = 'firefox';
  } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    browser = 'safari';
  } else if (userAgent.includes('edg')) {
    browser = 'edge';
  }

  // Detect OS
  let os = 'unknown';
  if (userAgent.includes('mac')) {
    os = 'mac';
  } else if (userAgent.includes('win')) {
    os = 'windows';
  } else if (userAgent.includes('linux')) {
    os = 'linux';
  }

  return `${platform}-${browser}-${os}`;
};

const buildSupportRequestLink = (user: IUserProfile | null) => {
  // const userLang = user?.lang ? user.lang.toLowerCase() : 'de';
  // TODO: FIx this language issue and remove this hardcoding
  const userLang = 'de';
  const link =
    supportRequestLinks[userLang as keyof typeof supportRequestLinks];

  const params = ['?'];

  if (user) {
    params.push(`alea_user_id=${encodeURIComponent(user.id)}&`);
  }

  params.push(`alea_user_id_infos=${encodeURIComponent(getPlatformInfo())}`);

  return link.concat(params.join(''));
};

const PageFooter: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const supportLink = buildSupportRequestLink(user);

  return (
    <Flex
      as="footer"
      minH="62px"
      backgroundColor="alea.500"
      boxShadow="0 -2px 4px rgba(0, 0, 0, 0.1)"
      mt={3}
      alignItems={'center'}
      py="18px"
      fontSize="15px"
    >
      <Flex
        height="100%"
        alignItems="center"
        justifyContent="space-between"
        px="25px"
        flexWrap={'wrap'}
        w={'100%'}
        gap="15px"
      >
        {/* Left side - Copyright and basic links */}
        <Flex alignItems="center" wrap={'wrap'}>
          <Text color="white" fontSize="15px" mr="20px">
            {t('footerCopyright')}
          </Text>
          <Link
            href={FooterLinks.TERMS_OF_USE}
            target="_blank"
            rel="noopener noreferrer"
            color="white"
            _hover={{ textDecoration: 'underline' }}
            mr="20px"
          >
            {t('footerTermsOfUse')}
          </Link>
          <Link
            href={FooterLinks.PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            color="white"
            _hover={{ textDecoration: 'underline' }}
            mr="20px"
          >
            {t('footerPrivacyPolicy')}
          </Link>
        </Flex>

        {/* Right side - Other links */}
        <Flex flexWrap={'wrap'}>
          <Link
            href={FooterLinks.FAQ}
            target="_blank"
            rel="noopener noreferrer"
            color="white"
            _hover={{ textDecoration: 'underline' }}
            mr="25px"
          >
            {t('footerFaq')}
          </Link>
          <Link
            href={supportLink}
            target="_blank"
            rel="noopener noreferrer"
            color="white"
            _hover={{ textDecoration: 'underline' }}
            mr="25px"
          >
            {t('footerSubmitSupportRequest')}
          </Link>
          <Link
            href={FooterLinks.INSTRUCTIONS_FOR_USE}
            target="_blank"
            rel="noopener noreferrer"
            color="white"
            _hover={{ textDecoration: 'underline' }}
            mr="25px"
          >
            {t('footerInstructionsForUse')}
          </Link>
          <Link
            href={FooterLinks.ALEA_COCKPIT_GUIDE}
            target="_blank"
            rel="noopener noreferrer"
            color="white"
            _hover={{ textDecoration: 'underline' }}
            mr="25px"
          >
            {t('footerAleaCockpitGuide')}
          </Link>
          <Link
            href={FooterLinks.ABOUT_ASSISTME}
            target="_blank"
            rel="noopener noreferrer"
            color="white"
            _hover={{ textDecoration: 'underline' }}
          >
            {t('footerAboutAssistme')}
          </Link>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default PageFooter;
